import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight, Bell, KeyRound, SearchCheck, ShieldCheck } from 'lucide-react'
import { buttonClassName } from '@/components/generals/button'
import { SectionHeading } from '@/components/landing/section-heading'

const services = [
    {
        icon: SearchCheck,
        title: 'Análise do seu objetivo',
        description: 'Entendemos o que você quer conquistar e indicamos crédito, prazo e grupo que cabem no orçamento.'
    },
    {
        icon: ShieldCheck,
        title: 'Administradoras autorizadas',
        description: 'Trabalhamos com administradoras autorizadas e fiscalizadas pelo Banco Central do Brasil.'
    },
    {
        icon: Bell,
        title: 'Acompanhamento mensal',
        description: 'Avisamos sobre assembleias, resultados e oportunidades de lance para você não perder a vez.'
    },
    {
        icon: KeyRound,
        title: 'Apoio na hora da compra',
        description: 'Quando chegar a sua vez, ajudamos com a documentação e o uso da carta de crédito.'
    }
]

export const ConsultingSection: FC = (): ReactNode => {
    return (
        <section id='consultoria' aria-labelledby='consultoria-titulo' className='py-24 sm:py-32'>
            <div className='mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20'>
                <div className='relative mx-auto mb-8 w-full max-w-md lg:mb-0 lg:max-w-none'>
                    <div className='relative aspect-[4/5] overflow-hidden rounded-[2rem] bg-sand shadow-xl shadow-ink/10'>
                        <Image
                            src='/images/consultoria.jpg'
                            alt='Consultor apresentando uma proposta impressa para uma cliente em uma mesa de reunião'
                            fill
                            sizes='(min-width: 1024px) 40vw, (min-width: 640px) 448px, 100vw'
                            className='object-cover object-[50%_40%]'
                        />
                    </div>

                    <div className='absolute -bottom-8 left-4 right-4 rounded-2xl bg-ink p-5 text-primary-foreground shadow-xl sm:left-auto sm:right-[-1.5rem] sm:w-72'>
                        <p className='font-heading text-3xl leading-tight text-gold'>
                            Do primeiro contato
                        </p>

                        <p className='mt-1 text-sm text-primary-foreground/75'>
                            até a chave na mão, com acompanhamento em cada etapa.
                        </p>
                    </div>
                </div>

                <div>
                    <SectionHeading
                        id='consultoria-titulo'
                        eyebrow='Consultoria'
                        title='Um especialista ao seu lado até a contemplação'
                        description='Consórcio é planejamento. Por isso você conta com um consultor que acompanha o grupo, as assembleias e o melhor momento para ofertar lance.'
                    />

                    <ul className='mt-10 grid gap-x-8 gap-y-7 sm:grid-cols-2'>
                        {services.map(service => (
                            <li key={service.title}>
                                <span className='grid size-11 place-items-center rounded-xl bg-sand text-bronze'>
                                    <service.icon aria-hidden='true' className='size-5' />
                                </span>

                                <p className='mt-4 font-bold text-foreground'>
                                    {service.title}
                                </p>

                                <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                                    {service.description}
                                </p>
                            </li>
                        ))}
                    </ul>

                    <Link href='#contato' className={buttonClassName({ size: 'lg', className: 'mt-11' })}>
                        Falar com um especialista
                        <ArrowRight aria-hidden='true' className='size-5' />
                    </Link>
                </div>
            </div>
        </section>
    )
}
